import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, MapPin, Music2, Sparkles, Zap, Users, Tag, Briefcase, ShoppingBag, Mic, BookOpen } from "lucide-react";
import { api, fileUrl } from "../lib/api";

// Config for how each entity type appears in the suggestions dropdown.
// Kept here so navbar, hero, and search page all render identical rows.
export const SUGGEST_GROUPS = [
  { key: "events",      Icon: Calendar,    label: "Events",      path: (x) => `/events/${x.slug}`,          title: (x) => x.title, sub: (x) => [x.venue_name, x.city].filter(Boolean).join(" · ") },
  { key: "venues",      Icon: MapPin,      label: "Venues",      path: (x) => `/venues/${x.slug}`,          title: (x) => x.name,  sub: (x) => [x.city, x.state].filter(Boolean).join(", ") },
  { key: "artists",     Icon: Music2,      label: "Artists",     path: (x) => `/artists/${x.slug}`,         title: (x) => x.name,  sub: (x) => (x.genres || [x.genre]).filter(Boolean).slice(0, 2).join(" · ") },
  { key: "attractions", Icon: Sparkles,    label: "Attractions", path: (x) => `/attractions/${x.slug}`,     title: (x) => x.name,  sub: (x) => [x.attraction_type, x.city].filter(Boolean).join(" · ") },
  { key: "activities",  Icon: Zap,         label: "Activities",  path: (x) => `/activities/${x.slug}`,      title: (x) => x.name,  sub: (x) => [x.activity_type, x.city].filter(Boolean).join(" · ") },
  { key: "businesses",  Icon: Users,       label: "Businesses",  path: (x) => `/businesses/${x.slug}`,      title: (x) => x.name,  sub: (x) => [x.business_type, x.city].filter(Boolean).join(" · ") },
  { key: "deals",       Icon: Tag,         label: "Deals",       path: (x) => `/deals/${x.slug || x.id}`,   title: (x) => x.title, sub: (x) => x.category || "Deal" },
  { key: "hiring_jobs", Icon: Briefcase,   label: "Jobs",        path: (x) => `/hiring/${x.slug}`,          title: (x) => x.title, sub: (x) => [x.job_category, x.city].filter(Boolean).join(" · ") },
  { key: "products",    Icon: ShoppingBag, label: "Marketplace", path: (x) => `/marketplace/${x.slug}`,     title: (x) => x.name,  sub: (x) => x.product_type || "Product" },
  { key: "gigs",        Icon: Mic,         label: "Gigs",        path: (x) => `/gigs/${x.slug || x.id}`,    title: (x) => x.title, sub: (x) => [x.gig_type, x.city].filter(Boolean).join(" · ") },
  { key: "kb",          Icon: BookOpen,    label: "Knowledge",   path: (x) => `/knowledge-base/${x.slug}`,  title: (x) => x.title, sub: (x) => x.summary || "" },
];

// Debounced live-search hook. Returns { loading, items } where items is a flat
// array of up to 8 hits across all categories, each annotated with __group.
export function useSuggestSearch(q, { minChars = 2, debounceMs = 250, max = 8 } = {}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef(null);
  useEffect(() => {
    const term = (q || "").trim();
    if (term.length < minChars) { setItems([]); setLoading(false); return; }
    setLoading(true);
    const t = setTimeout(() => {
      if (abortRef.current) abortRef.current.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      api.get(`/search?q=${encodeURIComponent(term)}`, { signal: ctrl.signal })
        .then((r) => {
          const data = r.data || {};
          const flat = [];
          for (const g of SUGGEST_GROUPS) {
            for (const it of (data[g.key] || []).slice(0, 3)) {
              flat.push({ ...it, __group: g });
              if (flat.length >= max) break;
            }
            if (flat.length >= max) break;
          }
          setItems(flat);
          setLoading(false);
        })
        .catch((err) => { if (err.name !== "CanceledError" && err.code !== "ERR_CANCELED") { setItems([]); setLoading(false); } });
    }, debounceMs);
    return () => clearTimeout(t);
  }, [q, minChars, debounceMs, max]);
  return { loading, items };
}

// Reusable dropdown that renders the suggestions list. Position it inside a
// relative wrapper. Consumer decides width & anchoring via className.
export function SuggestDropdown({ query, items, loading, onPick, onSeeAll, className = "", testidPrefix = "search" }) {
  const term = (query || "").trim();
  if (term.length < 2) return null;
  return (
    <div className={`bg-[#111] border border-white/10 rounded-2xl shadow-2xl shadow-black/50 overflow-hidden z-50 ${className}`} data-testid={`${testidPrefix}-dropdown`}>
      {loading && items.length === 0 && (
        <div className="p-4 text-sm text-zinc-500 flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-full border-2 border-rose-400 border-t-transparent animate-spin" /> Searching…
        </div>
      )}
      {!loading && items.length === 0 && (
        <div className="p-4 text-sm text-zinc-500">No matches — try a different keyword.</div>
      )}
      {items.length > 0 && (
        <ul className="max-h-96 overflow-y-auto py-1" data-testid={`${testidPrefix}-results`}>
          {items.map((it, i) => {
            const g = it.__group;
            const I = g.Icon;
            const title = g.title(it);
            const sub = g.sub(it);
            const img = fileUrl(it.profile_image || it.event_image || it.hero_image || it.logo || it.image_url || (it.images && it.images[0]));
            return (
              <li key={`${g.key}-${it.id || it.slug || i}`}>
                <button
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); onPick(g.path(it)); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-white/[0.05] text-left transition-colors"
                  data-testid={`${testidPrefix}-item-${g.key}-${it.slug || it.id || i}`}
                >
                  {img ? (
                    <img src={img} alt="" loading="lazy" className="w-9 h-9 rounded-lg object-cover border border-white/10 shrink-0" />
                  ) : (
                    <div className="w-9 h-9 rounded-lg bg-rose-500/15 border border-white/10 text-rose-300 flex items-center justify-center shrink-0"><I size={15} /></div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-white font-semibold leading-tight truncate">{title}</p>
                    <p className="text-[11px] text-zinc-500 mt-0.5 truncate">
                      <span className="text-rose-300 font-medium">{g.label}</span>
                      {sub ? <> · {sub}</> : null}
                    </p>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <button
        type="button"
        onMouseDown={(e) => { e.preventDefault(); onSeeAll(); }}
        className="w-full text-center text-xs font-bold text-rose-300 hover:text-rose-200 hover:bg-white/[0.03] border-t border-white/10 py-2.5 transition-colors"
        data-testid={`${testidPrefix}-see-all`}
      >
        See all results for "{term}" →
      </button>
    </div>
  );
}

// Outside-click hook to close a dropdown when clicking anywhere outside `ref`.
export function useOutsideClose(ref, onClose) {
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [ref, onClose]);
}
